import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2, Download, FileDown, Search, ArrowUp, ArrowDown, Wallet, Columns3, MessageCircle, AlarmClock, Sparkles } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { DatePicker } from "@/components/DatePicker";
import { useAuth } from "@/context/AuthContext";

const empty = { date: new Date().toISOString().slice(0, 10), description: "", party_id: "none", category_id: "none", type: "credit", amount: "", actual: "", due_date: "" };

export default function Ledger() {
  const { isEditor, isAdmin, currency, fmt } = useAuth();
  const formatPKR = fmt;
  const CUR_CODE = currency?.code || "PKR";
  const [entries, setEntries] = useState([]);
  const [parties, setParties] = useState([]);
  const [categories, setCategories] = useState([]);
  const [biz, setBiz] = useState(null);
  // Default filter: current month (1st → last day). User can clear/change via Reset.
  const currentMonthRange = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const start = new Date(y, m, 1);
    const end = new Date(y, m + 1, 0); // last day of current month
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { start_date: fmt(start), end_date: fmt(end) };
  };
  const [filters, setFilters] = useState({ ...currentMonthRange(), type: "all", party_id: "all", category_id: "all", search: "" });
  const [sortDir, setSortDir] = useState("desc"); // "desc" newest first, "asc" oldest first
  // Column visibility — persisted per-browser. Toggle from the Columns button.
  const COL_KEYS = ["date", "description", "party", "category", "credit", "debit", "pending", "balance", "created_by", "actions"];
  const COL_LABELS = {
    date: "Date", description: "Description", party: "Party", category: "Category",
    credit: "Credit", debit: "Debit", pending: "Pending", balance: "Balance",
    created_by: "Created By", actions: "Actions",
  };
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const raw = localStorage.getItem("ledger_visible_cols_v1");
      if (raw) {
        const saved = JSON.parse(raw);
        return COL_KEYS.reduce((acc, k) => ({ ...acc, [k]: saved[k] !== false }), {});
      }
    } catch {}
    return COL_KEYS.reduce((acc, k) => ({ ...acc, [k]: true }), {});
  });
  useEffect(() => {
    try { localStorage.setItem("ledger_visible_cols_v1", JSON.stringify(visibleCols)); } catch {}
  }, [visibleCols]);
  const toggleCol = (key) => setVisibleCols((v) => ({ ...v, [key]: !v[key] }));
  const isVisible = (k) => visibleCols[k] !== false;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPicks, setBulkPicks] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    api.get("/business/me").then((r) => setBiz(r.data)).catch(() => {});
  }, []);

  const fetchAll = async () => {
    const params = {};
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    if (filters.type !== "all") params.type = filters.type;
    if (filters.party_id !== "all") params.party_id = filters.party_id;
    if (filters.category_id !== "all") params.category_id = filters.category_id;
    if (filters.search) params.search = filters.search;
    const [e, p, c] = await Promise.all([
      api.get("/entries", { params }),
      api.get("/parties"),
      api.get("/categories"),
    ]);
    setEntries(e.data); setParties(p.data); setCategories(c.data);
  };

  useEffect(() => { fetchAll(); }, [filters.start_date, filters.end_date, filters.type, filters.party_id, filters.category_id, filters.search]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (en) => {
    setEditing(en);
    setForm({
      date: en.date, description: en.description || "",
      party_id: en.party_id || "none", category_id: en.category_id || "none",
      type: en.type, amount: String(en.amount),
      actual: en.actual !== undefined && en.actual !== null ? String(en.actual) : String(en.amount),
      due_date: en.due_date || "",
    });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const amountNum = parseFloat(form.amount);
    const actualNum = form.actual === "" || form.actual === null ? amountNum : parseFloat(form.actual);
    const payload = {
      date: form.date,
      description: form.description,
      type: form.type,
      amount: amountNum,
      actual: isNaN(actualNum) ? amountNum : actualNum,
      party_id: form.party_id === "none" ? null : form.party_id,
      category_id: form.category_id === "none" ? null : form.category_id,
      due_date: form.due_date || null,
    };
    try {
      if (editing) {
        await api.put(`/entries/${editing.id}`, payload);
        toast.success("Entry updated");
      } else {
        await api.post("/entries", payload);
        toast.success("Entry added");
      }
      setOpen(false);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setLoading(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/entries/${id}`);
    toast.success("Entry deleted");
    fetchAll();
  };

  const fmtNum = (n) => Number(n || 0).toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const exportCSV = () => {
    // Honor column visibility — hidden columns are excluded from CSV
    const colDefs = [
      { key: "date", header: "Date", value: (e) => e.date },
      { key: "description", header: "Description", value: (e) => e.description || "" },
      { key: "party", header: "Party", value: (e) => e.party_name || "" },
      { key: "category", header: "Category", value: (e) => e.category_name || "" },
      { key: "credit", header: `Credit (${CUR_CODE})`, value: (e) => e.type === "credit" ? (e.actual ?? e.amount) : "" },
      { key: "debit", header: `Debit (${CUR_CODE})`, value: (e) => e.type === "debit" ? (e.actual ?? e.amount) : "" },
      { key: "pending", header: `Pending (${CUR_CODE})`, value: (e) => e.party_running_kind ? e.party_running_pending : "" },
      { key: "pending", header: "Pending Kind", value: (e) => e.party_running_kind || "" },
      { key: "balance", header: `Balance (${CUR_CODE})`, value: (e) => e.balance },
    ];
    if (isAdmin) colDefs.push({ key: "created_by", header: "Created By", value: (e) => e.created_by_name || "—" });
    const activeCols = colDefs.filter((c) => isVisible(c.key));
    const headers = activeCols.map((c) => c.header);
    const rows = sortedEntries.map((e) => activeCols.map((c) => c.value(e)));
    // CSV escape helper
    const esc = (c) => `"${String(c ?? "").replace(/"/g, '""')}"`;
    const toRow = (arr) => arr.map(esc).join(",");

    // Branded header rows (mirror PDF header)
    const headerLines = [];
    headerLines.push([biz?.name || "Cashflow Ledger"]);
    const subParts = [];
    if (biz?.address) subParts.push(biz.address);
    if (biz?.phone) subParts.push(`Phone: ${biz.phone}`);
    if (biz?.email) subParts.push(`Email: ${biz.email}`);
    if (biz?.tax_id) subParts.push(`Tax ID: ${biz.tax_id}`);
    if (subParts.length) headerLines.push([subParts.join(" · ")]);
    headerLines.push(["Cashflow Ledger Statement"]);
    headerLines.push([`Generated: ${new Date().toLocaleString()}`]);
    // Filters summary
    const filterParts = [];
    if (filters.start_date) filterParts.push(`From: ${filters.start_date}`);
    if (filters.end_date) filterParts.push(`To: ${filters.end_date}`);
    if (filters.type && filters.type !== "all") filterParts.push(`Type: ${filters.type}`);
    if (filters.party_id && filters.party_id !== "all") {
      const p = parties.find((x) => x.id === filters.party_id);
      if (p) filterParts.push(`Party: ${p.name}`);
    }
    if (filters.category_id && filters.category_id !== "all") {
      const c = categories.find((x) => x.id === filters.category_id);
      if (c) filterParts.push(`Category: ${c.name}`);
    }
    if (filters.search) filterParts.push(`Search: ${filters.search}`);
    if (filterParts.length) headerLines.push([`Filters: ${filterParts.join(" | ")}`]);
    // Totals
    headerLines.push([`Totals — Credit: ${fmtNum(totals.credit)} | Debit: ${fmtNum(totals.debit)} | Net: ${fmtNum(totals.net)} | Receivable: ${fmtNum(totals.receivable)} | Payable: ${fmtNum(totals.payable)}`]);
    headerLines.push([""]); // blank separator row

    const csv = [
      ...headerLines.map(toRow),
      toRow(headers),
      ...rows.map(toRow),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ledger-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    let cursorY = 14;
    let titleX = 14;

    // Logo (if available) — left side
    if (biz?.logo_data_uri) {
      try {
        const fmt = (biz.logo_data_uri.match(/^data:image\/(png|jpeg|jpg|webp);base64/i) || [])[1];
        if (fmt) {
          doc.addImage(biz.logo_data_uri, fmt.toUpperCase() === "JPG" ? "JPEG" : fmt.toUpperCase(), 14, 8, 14, 14);
          titleX = 32;
        }
      } catch (_) { /* ignore bad image */ }
    }

    // Business name + title
    doc.setFontSize(14);
    doc.setTextColor(15, 23, 42);
    doc.text(biz?.name || "Cashflow Ledger", titleX, cursorY);
    doc.setFontSize(9);
    doc.setTextColor(107, 114, 128);
    cursorY += 5;
    const subParts = [];
    if (biz?.address) subParts.push(biz.address);
    if (biz?.phone) subParts.push(biz.phone);
    if (biz?.email) subParts.push(biz.email);
    if (biz?.tax_id) subParts.push(`Tax ID: ${biz.tax_id}`);
    if (subParts.length) doc.text(subParts.join("   ·   "), titleX, cursorY);

    // Right-aligned meta
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFontSize(8);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 14, 12, { align: "right" });
    doc.text("Cashflow Ledger Statement", pageWidth - 14, 18, { align: "right" });

    // Horizontal divider
    doc.setDrawColor(229, 231, 235);
    doc.line(14, 26, pageWidth - 14, 26);

    const colDefs = [
      { key: "date", header: "Date", value: (e) => e.date, align: "left" },
      { key: "description", header: "Description", value: (e) => e.description || "—", align: "left" },
      { key: "party", header: "Party", value: (e) => e.party_name || "—", align: "left" },
      { key: "category", header: "Category", value: (e) => e.category_name || "—", align: "left" },
      { key: "credit", header: `Credit (${CUR_CODE})`, value: (e) => e.type === "credit" ? fmtNum(e.actual ?? e.amount) : "", align: "right", color: [16, 185, 129] },
      { key: "debit", header: `Debit (${CUR_CODE})`, value: (e) => e.type === "debit" ? fmtNum(e.actual ?? e.amount) : "", align: "right", color: [239, 68, 68] },
      { key: "pending", header: `Pending (${CUR_CODE})`, value: (e) => e.party_running_kind ? `${fmtNum(e.party_running_pending)} ${e.party_running_kind === "receivable" ? "(R)" : "(P)"}` : "", align: "right", isPending: true },
      { key: "balance", header: `Balance (${CUR_CODE})`, value: (e) => fmtNum(e.balance), align: "right" },
    ];
    if (isAdmin) colDefs.push({ key: "created_by", header: "Created By", value: (e) => e.created_by_name || "—", align: "left" });
    const activeCols = colDefs.filter((c) => isVisible(c.key));
    const pendingColIdx = activeCols.findIndex((c) => c.isPending);
    const head = activeCols.map((c) => c.header);
    const columnStyles = {};
    activeCols.forEach((c, i) => {
      const s = {};
      if (c.align) s.halign = c.align;
      if (c.color) s.textColor = c.color;
      columnStyles[i] = s;
    });
    autoTable(doc, {
      startY: 30,
      head: [head],
      body: sortedEntries.map((e) => activeCols.map((c) => c.value(e))),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [15, 23, 42], textColor: 255 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      columnStyles,
      didParseCell: (data) => {
        // Color pending cells: Blue = Receivable, Red = Payable (per-party running net)
        if (data.section === "body" && pendingColIdx >= 0 && data.column.index === pendingColIdx) {
          const e = sortedEntries[data.row.index];
          if (e && e.party_running_kind === "receivable") {
            data.cell.styles.textColor = [37, 99, 235]; // blue
            data.cell.styles.fontStyle = "bold";
          } else if (e && e.party_running_kind === "payable") {
            data.cell.styles.textColor = [239, 68, 68]; // red
            data.cell.styles.fontStyle = "bold";
          }
        }
      },
      didDrawPage: (data) => {
        // Footer with page number on every page
        const pageCount = doc.internal.getNumberOfPages();
        const pageH = doc.internal.pageSize.getHeight();
        doc.setFontSize(7);
        doc.setTextColor(156, 163, 175);
        const footerLeft = biz?.name ? `${biz.name} · Confidential` : "Cashflow Ledger";
        doc.text(footerLeft, 14, pageH - 6);
        doc.text(`Page ${data.pageNumber} / ${pageCount}`, pageWidth - 14, pageH - 6, { align: "right" });
      },
    });
    doc.save(`ledger-${new Date().toISOString().slice(0,10)}.pdf`);
  };

  const totals = useMemo(() => {
    let c = 0, d = 0;
    // Per-party net: take the latest party_running for each party within the filtered set.
    // Entries are reverse-chronological (latest first), so the FIRST occurrence per party = latest.
    const latestPartyRunning = new Map();
    entries.forEach((e) => {
      const cash = e.actual !== undefined && e.actual !== null ? e.actual : e.amount;
      if (e.type === "credit") c += cash; else d += cash;
      const key = e.party_id || "__none__";
      if (!latestPartyRunning.has(key)) {
        // Sign: receivable → positive, payable → negative
        const signed = e.party_running_kind === "receivable" ? (e.party_running_pending || 0)
                       : e.party_running_kind === "payable" ? -(e.party_running_pending || 0)
                       : 0;
        latestPartyRunning.set(key, signed);
      }
    });
    let recv = 0, pay = 0;
    latestPartyRunning.forEach((v) => {
      if (v > 0) recv += v;
      else if (v < 0) pay += -v;
    });
    return { credit: c, debit: d, net: c - d, receivable: recv, payable: pay };
  }, [entries]);

  const sortedEntries = useMemo(() => {
    const arr = [...entries];
    arr.sort((a, b) => {
      const da = `${a.date} ${a.created_at || ""}`;
      const db = `${b.date} ${b.created_at || ""}`;
      return sortDir === "asc" ? da.localeCompare(db) : db.localeCompare(da);
    });
    return arr;
  }, [entries, sortDir]);

  // Identify the LATEST entry (chronologically) per party — that row reflects the
  // CURRENT outstanding pending for the party. Only show "Settle" on those rows
  // so the user can't accidentally trigger settlement against a stale historical snapshot.
  const settleableIds = useMemo(() => {
    const latestChronoPerParty = new Map();
    [...entries]
      .sort((a, b) => `${a.date} ${a.created_at || ""}`.localeCompare(`${b.date} ${b.created_at || ""}`))
      .forEach((e) => {
        const key = e.party_id || "__none__";
        latestChronoPerParty.set(key, e);
      });
    const set = new Set();
    latestChronoPerParty.forEach((e) => {
      if (e.party_running_kind && e.party_running_pending > 0 && e.party_id) {
        set.add(e.id);
      }
    });
    return set;
  }, [entries]);

  const partyMap = useMemo(() => {
    const m = new Map();
    parties.forEach((p) => m.set(p.id, p));
    return m;
  }, [parties]);

  const todayIso = new Date().toISOString().slice(0, 10);
  const isOverdue = (e) => !!(e.due_date && e.due_date < todayIso && e.party_running_kind === "receivable");

  const openWhatsApp = (e) => {
    const party = partyMap.get(e.party_id) || {};
    const phone = (party.contact || "").replace(/[^0-9]/g, "");
    const msg = `Hello ${party.name || ""}, this is a friendly reminder from ${biz?.name || "us"}.\n\nYou have an outstanding balance of Rs ${Number(e.party_running_pending).toLocaleString()} ` +
                `against "${e.description || `entry on ${e.date}`}"${e.due_date ? ` (due ${e.due_date})` : ""}.\n\nPlease let us know when we can expect settlement. Thank you!`;
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openSettle = (en) => {
    // Pre-fill a settlement entry: AMOUNT=0, ACTUAL=current net pending, opposite-side type.
    // Receivable from party → CREDIT (they pay us). Payable to party → DEBIT (we pay them).
    const settleType = en.party_running_kind === "receivable" ? "credit" : "debit";
    setEditing(null);
    setForm({
      date: new Date().toISOString().slice(0, 10),
      description: `Settlement against ${en.description || `entry on ${en.date}`}`,
      party_id: en.party_id,
      category_id: "none",
      type: settleType,
      amount: "0",
      actual: String(en.party_running_pending),
      due_date: "",
    });
    setOpen(true);
  };

  // All parties with an outstanding net pending — used by Bulk Settle
  const bulkSettleable = useMemo(() => {
    const map = new Map();
    [...entries]
      .sort((a, b) => `${a.date} ${a.created_at || ""}`.localeCompare(`${b.date} ${b.created_at || ""}`))
      .forEach((e) => {
        if (!e.party_id) return;
        map.set(e.party_id, e); // latest per party
      });
    const list = [];
    map.forEach((e) => {
      if (e.party_running_kind && e.party_running_pending > 0) {
        list.push({
          party_id: e.party_id,
          party_name: e.party_name || "—",
          kind: e.party_running_kind,
          amount: e.party_running_pending,
          last_entry_id: e.id,
          last_entry_desc: e.description || `entry on ${e.date}`,
        });
      }
    });
    return list.sort((a, b) => b.amount - a.amount);
  }, [entries]);

  const openBulkSettle = () => {
    setBulkPicks(bulkSettleable.reduce((acc, x) => ({ ...acc, [x.party_id]: true }), {}));
    setBulkOpen(true);
  };

  const submitBulkSettle = async () => {
    setBulkBusy(true);
    const todayIsoStr = new Date().toISOString().slice(0, 10);
    let ok = 0, fail = 0;
    for (const it of bulkSettleable) {
      if (!bulkPicks[it.party_id]) continue;
      const settleType = it.kind === "receivable" ? "credit" : "debit";
      try {
        await api.post("/entries", {
          date: todayIsoStr,
          description: `Bulk settlement against ${it.last_entry_desc}`,
          type: settleType,
          amount: 0,
          actual: it.amount,
          party_id: it.party_id,
          category_id: null,
          due_date: null,
        });
        ok++;
      } catch {
        fail++;
      }
    }
    setBulkBusy(false);
    setBulkOpen(false);
    if (ok) toast.success(`Created ${ok} settlement ${ok === 1 ? "entry" : "entries"}${fail ? `, ${fail} failed` : ""}`);
    else if (fail) toast.error(`All ${fail} settlements failed`);
    await fetchAll();
  };

  return (
    <div className="p-10 max-w-[1400px]" data-testid="ledger-page">
      <PageHeader overline="Transactions" title="Ledger Sheet" testid="ledger-header">
        {isEditor && bulkSettleable.length > 0 && (
          <Button onClick={openBulkSettle} data-testid="bulk-settle-button" variant="outline"
            className="rounded-none border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white h-10 text-xs uppercase tracking-wider font-semibold">
            <Sparkles className="h-3.5 w-3.5 mr-2" /> Bulk Settle ({bulkSettleable.length})
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              data-testid="columns-toggle-button"
              className="rounded-none border-[#E5E7EB] h-10 text-xs uppercase tracking-wider font-semibold"
            >
              <Columns3 className="h-3.5 w-3.5 mr-2" /> Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-none w-52" data-testid="columns-menu">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">
              Show / hide columns
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {COL_KEYS.filter((k) => k !== "created_by" || isAdmin).map((k) => (
              <DropdownMenuCheckboxItem
                key={k}
                checked={isVisible(k)}
                onCheckedChange={() => toggleCol(k)}
                onSelect={(e) => e.preventDefault()}
                data-testid={`col-toggle-${k}`}
                className="text-sm rounded-none cursor-pointer"
              >
                {COL_LABELS[k]}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button onClick={exportCSV} data-testid="export-csv-button" variant="outline"
          className="rounded-none border-[#E5E7EB] h-10 text-xs uppercase tracking-wider font-semibold">
          <Download className="h-3.5 w-3.5 mr-2" /> CSV
        </Button>
        <Button onClick={exportPDF} data-testid="export-pdf-button" variant="outline"
          className="rounded-none border-[#E5E7EB] h-10 text-xs uppercase tracking-wider font-semibold">
          <FileDown className="h-3.5 w-3.5 mr-2" /> PDF
        </Button>
        {isEditor && (
          <Button onClick={openNew} data-testid="add-entry-button"
            className="rounded-none bg-[#0F172A] hover:bg-[#1E293B] text-white h-10 text-xs uppercase tracking-wider font-semibold">
            + New Entry
          </Button>
        )}
      </PageHeader>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-6 p-5 border border-[#E5E7EB] bg-[#F9FAFB]" data-testid="filters-bar">
        <div>
          <Label className="overline mb-1.5 block">From</Label>
          <DatePicker
            value={filters.start_date}
            onChange={(v) => setFilters({ ...filters, start_date: v })}
            placeholder="Any date"
            clearable
            testid="filter-start-date"
          />
        </div>
        <div>
          <Label className="overline mb-1.5 block">To</Label>
          <DatePicker
            value={filters.end_date}
            onChange={(v) => setFilters({ ...filters, end_date: v })}
            placeholder="Any date"
            clearable
            testid="filter-end-date"
          />
        </div>
        <div>
          <Label className="overline mb-1.5 block">Type</Label>
          <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
            <SelectTrigger data-testid="filter-type" className="rounded-none h-10 border-[#E5E7EB] bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="credit">Credit</SelectItem>
              <SelectItem value="debit">Debit</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline mb-1.5 block">Party</Label>
          <Select value={filters.party_id} onValueChange={(v) => setFilters({ ...filters, party_id: v })}>
            <SelectTrigger data-testid="filter-party" className="rounded-none h-10 border-[#E5E7EB] bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All Parties</SelectItem>
              {parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline mb-1.5 block">Category</Label>
          <Select value={filters.category_id} onValueChange={(v) => setFilters({ ...filters, category_id: v })}>
            <SelectTrigger data-testid="filter-category" className="rounded-none h-10 border-[#E5E7EB] bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="lg:col-span-2">
          <Label className="overline mb-1.5 block">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
            <Input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              data-testid="filter-search" placeholder="Search description…"
              className="rounded-none h-10 pl-9 border-[#E5E7EB] bg-white" />
          </div>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-[10px] mb-6">
        <div className="bg-white border border-[#E3E3E3] p-3">
          <div className="overline">Filtered Credit</div>
          <div className="font-mono text-base font-semibold text-[#10B981] mt-1" data-testid="totals-credit">{formatPKR(totals.credit)}</div>
        </div>
        <div className="bg-white border border-[#E3E3E3] p-3">
          <div className="overline">Filtered Debit</div>
          <div className="font-mono text-base font-semibold text-[#EF4444] mt-1" data-testid="totals-debit">{formatPKR(totals.debit)}</div>
        </div>
        <div className="bg-white border border-[#E3E3E3] p-3">
          <div className="overline">Net Cash</div>
          <div className={`font-mono text-base font-semibold mt-1 ${totals.net >= 0 ? "text-[#0F172A]" : "text-[#EF4444]"}`} data-testid="totals-net">
            {formatPKR(totals.net)}
          </div>
        </div>
        <div className="bg-white border border-[#E3E3E3] p-3">
          <div className="overline">Receivable</div>
          <div className="font-mono text-base font-semibold text-[#2563EB] mt-1" data-testid="totals-receivable">{formatPKR(totals.receivable)}</div>
        </div>
        <div className="bg-white border border-[#E3E3E3] p-3">
          <div className="overline">Payable</div>
          <div className="font-mono text-base font-semibold text-[#EF4444] mt-1" data-testid="totals-payable">{formatPKR(totals.payable)}</div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-[#E3E3E3] bg-white overflow-x-auto" data-testid="ledger-table-wrapper">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E3E3E3] bg-[#F9FAFB]">
              {isVisible("date") && (
                <th className="text-left px-4 py-3 overline whitespace-nowrap w-[110px]">
                  <button
                    type="button"
                    onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                    data-testid="sort-date-button"
                    className="inline-flex items-center gap-1.5 hover:text-[#0F172A] transition-colors"
                    title={sortDir === "asc" ? "Sorted: Oldest first (click for newest)" : "Sorted: Newest first (click for oldest)"}
                  >
                    Date
                    {sortDir === "asc"
                      ? <ArrowUp className="h-3 w-3" data-testid="sort-asc-icon" />
                      : <ArrowDown className="h-3 w-3" data-testid="sort-desc-icon" />}
                  </button>
                </th>
              )}
              {isVisible("description") && <th className="text-left px-4 py-3 overline">Description</th>}
              {isVisible("party") && <th className="text-left px-4 py-3 overline">Party</th>}
              {isVisible("category") && <th className="text-left px-4 py-3 overline">Category</th>}
              {isVisible("credit") && <th className="text-right px-4 py-3 overline text-[#10B981]">Credit</th>}
              {isVisible("debit") && <th className="text-right px-4 py-3 overline text-[#EF4444]">Debit</th>}
              {isVisible("pending") && <th className="text-right px-4 py-3 overline">Pending</th>}
              {isVisible("balance") && <th className="text-right px-4 py-3 overline">Balance</th>}
              {isAdmin && isVisible("created_by") && <th className="text-left px-4 py-3 overline">Created By</th>}
              {isVisible("actions") && <th className="px-4 py-3 overline text-right">Actions</th>}
            </tr>
          </thead>
          <tbody data-testid="ledger-tbody">
            {sortedEntries.length === 0 && (
              <tr><td colSpan={COL_KEYS.filter((k) => isVisible(k) && (k !== "created_by" || isAdmin)).length} className="text-center py-16 text-[#6B7280]" data-testid="ledger-empty">
                No entries match your filters. <button onClick={openNew} className="text-[#0F172A] font-semibold border-b border-[#0F172A] ml-1">Add one</button>
              </td></tr>
            )}
            {sortedEntries.map((e) => (
              <tr key={e.id} className="border-b border-[#E3E3E3] hover:bg-[#F9FAFB]" data-testid={`ledger-row-${e.id}`}>
                {isVisible("date") && <td className="px-4 py-3 font-mono text-xs whitespace-nowrap w-[110px]">{e.date}</td>}
                {isVisible("description") && <td className="px-4 py-3 max-w-xs truncate" title={e.description}>{e.description || "—"}</td>}
                {isVisible("party") && <td className="px-4 py-3 text-[#4B5563]">{e.party_name || "—"}</td>}
                {isVisible("category") && <td className="px-4 py-3 text-[#4B5563]">{e.category_name || "—"}</td>}
                {isVisible("credit") && (
                  <td className="px-4 py-3 text-right font-mono font-semibold text-[#10B981]" data-testid={`credit-cell-${e.id}`}>
                    {e.type === "credit" ? formatPKR(e.actual ?? e.amount) : <span className="text-[#D1D5DB]">—</span>}
                  </td>
                )}
                {isVisible("debit") && (
                  <td className="px-4 py-3 text-right font-mono font-semibold text-[#EF4444]" data-testid={`debit-cell-${e.id}`}>
                    {e.type === "debit" ? formatPKR(e.actual ?? e.amount) : <span className="text-[#D1D5DB]">—</span>}
                  </td>
                )}
                {isVisible("pending") && (
                  <td className="px-4 py-3 text-right font-mono font-semibold" data-testid={`pending-cell-${e.id}`}>
                    {e.party_running_kind === "receivable" && (
                      <span className="text-[#2563EB] inline-flex items-center justify-end gap-1.5" title="Net receivable from this party as of this entry">
                        {isOverdue(e) && (
                          <span title={`Overdue since ${e.due_date}`} data-testid={`overdue-badge-${e.id}`}
                            className="bg-[#EF4444] text-white text-[9px] uppercase tracking-wider font-bold px-1 py-0.5">
                            <AlarmClock className="h-2.5 w-2.5 inline" /> Overdue
                          </span>
                        )}
                        {formatPKR(e.party_running_pending)}
                      </span>
                    )}
                    {e.party_running_kind === "payable" && (
                      <span className="text-[#EF4444]" title="Net amount we owe this party as of this entry">
                        {formatPKR(e.party_running_pending)}
                      </span>
                    )}
                    {!e.party_running_kind && <span className="text-[#D1D5DB]">—</span>}
                  </td>
                )}
                {isVisible("balance") && (
                  <td className={`px-4 py-3 text-right font-mono font-semibold ${e.balance < 0 ? "text-[#EF4444]" : "text-[#0F172A]"}`} data-testid={`balance-cell-${e.id}`}>
                    {formatPKR(e.balance)}
                  </td>
                )}
                {isAdmin && isVisible("created_by") && (
                  <td className="px-4 py-3 text-[#4B5563] text-xs" data-testid={`createdby-cell-${e.id}`}>
                    {e.created_by_name || "—"}
                  </td>
                )}
                {isVisible("actions") && (
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {isEditor ? (
                        <>
                          {settleableIds.has(e.id) && (
                            <button
                              onClick={() => openSettle(e)}
                              data-testid={`settle-entry-${e.id}`}
                              title={`Settle ${e.party_running_kind === "receivable" ? "₨ " + e.party_running_pending + " receivable from " : "₨ " + e.party_running_pending + " payable to "}${e.party_name || ""}`}
                              className={`p-1.5 ${e.party_running_kind === "receivable" ? "hover:bg-[#2563EB]/10 text-[#2563EB]" : "hover:bg-[#EF4444]/10 text-[#EF4444]"}`}
                            >
                              <Wallet className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {settleableIds.has(e.id) && e.party_running_kind === "receivable" && (
                            <button
                              onClick={() => openWhatsApp(e)}
                              data-testid={`whatsapp-entry-${e.id}`}
                              title={`Send WhatsApp reminder to ${e.party_name || ""}`}
                              className="p-1.5 hover:bg-[#25D366]/10 text-[#25D366]"
                            >
                              <MessageCircle className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button onClick={() => openEdit(e)} data-testid={`edit-entry-${e.id}`}
                            className="p-1.5 hover:bg-[#E5E7EB]"><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => onDelete(e.id)} data-testid={`delete-entry-${e.id}`}
                            className="p-1.5 hover:bg-[#EF4444]/10 text-[#EF4444]"><Trash2 className="h-3.5 w-3.5" /></button>
                        </>
                      ) : (
                        <span className="text-xs text-[#9CA3AF]">—</span>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg" data-testid="entry-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">{editing ? "Edit Entry" : "New Entry"}</DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              {editing ? "Update the details of this ledger entry." : "Record a new credit or debit transaction in the ledger."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4 mt-2" data-testid="entry-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline mb-1.5 block">Date</Label>
                <DatePicker
                  value={form.date}
                  onChange={(v) => setForm({ ...form, date: v })}
                  placeholder="Select date"
                  testid="entry-date"
                />
              </div>
              <div>
                <Label className="overline mb-1.5 block">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="entry-type" className="rounded-none h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="credit">Credit (Inflow)</SelectItem>
                    <SelectItem value="debit">Debit (Outflow)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline mb-1.5 block">Amount ({CUR_CODE})</Label>
                <Input type="number" step="0.01" min="0"
                  value={form.amount}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((f) => ({
                      ...f,
                      amount: v,
                      // Auto-sync actual when it was matching the old amount (the common "settled" case)
                      actual: (!f.actual || f.actual === f.amount) ? v : f.actual,
                    }));
                  }}
                  required
                  data-testid="entry-amount" placeholder="0.00 (or 0 for pure settlement)" className="rounded-none h-10 font-mono" />
                <p className="text-[10px] text-[#6B7280] mt-1">Total bill / invoice value. Use <strong>0</strong> when recording a pure cash settlement of old dues.</p>
              </div>
              <div>
                <Label className="overline mb-1.5 block">Actual ({CUR_CODE})</Label>
                <Input type="number" step="0.01" min="0"
                  value={form.actual}
                  onChange={(e) => setForm({ ...form, actual: e.target.value })}
                  data-testid="entry-actual" placeholder="0.00 (cash settled)" className="rounded-none h-10 font-mono" />
                <p className="text-[10px] text-[#6B7280] mt-1">
                  Cash actually {form.type === "credit" ? "received" : "paid"}. Diff with Amount becomes {form.type === "credit" ? "Receivable" : "Payable"}.
                </p>
              </div>
            </div>
            <div>
              <Label className="overline mb-1.5 block">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
                data-testid="entry-description" placeholder="e.g. Invoice #123 payment from ABC Corp" className="rounded-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline mb-1.5 block">Party</Label>
                <Select value={form.party_id} onValueChange={(v) => setForm({ ...form, party_id: v })}>
                  <SelectTrigger data-testid="entry-party" className="rounded-none h-10"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="none">— None —</SelectItem>
                    {parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline mb-1.5 block">Category</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger data-testid="entry-category" className="rounded-none h-10"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="none">— None —</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="overline mb-1.5 block">Due Date <span className="text-[10px] text-[#6B7280] normal-case">(optional — only for pending amounts)</span></Label>
              <DatePicker
                value={form.due_date}
                onChange={(v) => setForm({ ...form, due_date: v })}
                placeholder="When should this be settled?"
                testid="entry-due-date"
              />
            </div>
            <DialogFooter className="pt-3">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="entry-cancel"
                className="rounded-none h-10 text-xs uppercase tracking-wider">Cancel</Button>
              <Button type="submit" disabled={loading} data-testid="entry-save-button"
                className="rounded-none h-10 bg-[#0F172A] hover:bg-[#1E293B] text-xs uppercase tracking-wider">
                {loading ? "Saving…" : (editing ? "Update" : "Add Entry")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Settle Dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="bulk-settle-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">Bulk Settle Pending Dues</DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              Select parties to clear in one shot. A settlement entry (Amount = 0, Actual = pending) will be created for each, dated today. Uncheck any you don't want to settle right now.
            </DialogDescription>
          </DialogHeader>
          <div className="border border-[#E3E3E3] divide-y divide-[#E3E3E3] my-3">
            {bulkSettleable.length === 0 && (
              <div className="p-6 text-center text-sm text-[#6B7280]">No outstanding dues to settle.</div>
            )}
            {bulkSettleable.map((it) => (
              <label key={it.party_id} className="flex items-center justify-between gap-3 p-3 hover:bg-[#F9FAFB] cursor-pointer"
                data-testid={`bulk-row-${it.party_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <input
                    type="checkbox"
                    checked={!!bulkPicks[it.party_id]}
                    onChange={(e) => setBulkPicks((p) => ({ ...p, [it.party_id]: e.target.checked }))}
                    data-testid={`bulk-check-${it.party_id}`}
                    className="h-4 w-4 accent-[#0F172A]"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#111827] truncate">{it.party_name}</div>
                    <div className="text-[10px] text-[#6B7280] truncate">against {it.last_entry_desc}</div>
                  </div>
                </div>
                <div className={`font-mono text-sm font-semibold shrink-0 ${it.kind === "receivable" ? "text-[#2563EB]" : "text-[#EF4444]"}`}>
                  {it.kind === "receivable" ? "R" : "P"} {formatPKR(it.amount)}
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkBusy}
              data-testid="bulk-settle-cancel"
              className="rounded-none h-10 text-xs uppercase tracking-wider">Cancel</Button>
            <Button
              type="button" onClick={submitBulkSettle} disabled={bulkBusy || Object.values(bulkPicks).every((v) => !v)}
              data-testid="bulk-settle-confirm"
              className="rounded-none h-10 bg-[#2563EB] hover:bg-[#1E40AF] text-white text-xs uppercase tracking-wider">
              {bulkBusy ? "Settling…" : `Settle ${Object.values(bulkPicks).filter(Boolean).length} parties`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
